import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { AlertTriangle, ChevronLeft, ChevronRight, IndianRupee, Loader2, MessageSquarePlus } from "lucide-react";

import useApiMethods from "../../../lib/useApiMethods";
import { EMPLOYEE_MY_PENALTIES, PENALTY_APPEAL } from "../../../constants/api";
import ToastMsg from "../../../components/common/ToastMsg";
import { cn } from "../../../utils/tailwindUtils";
import { Button } from "../../../components/ui/button";
import { DialogModal } from "../../../components/ui/dialogModal";
import { Select } from "antd";

const { Option } = Select;

const APPEAL_TYPES = [
    { value: "penalty_unfair", label: "Penalty is unfair" },
    { value: "attendance_wrong", label: "Attendance record is wrong" },
    { value: "other", label: "Other" },
];

const STATUS_CHIP = {
    pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    waived: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
    reduced: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    rejected: "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300",
};

const MyProfilePenaltiesTab = () => {
    const { fetch, create } = useApiMethods();
    const fetchRef = useRef(fetch);
    const createRef = useRef(create);
    fetchRef.current = fetch;
    createRef.current = create;

    const [month, setMonth] = useState(dayjs().month() + 1);
    const [year, setYear] = useState(dayjs().year());
    const [loading, setLoading] = useState(true);
    const [penalties, setPenalties] = useState([]);
    const [summary, setSummary] = useState(null);
    const [appealOpen, setAppealOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [appealType, setAppealType] = useState("penalty_unfair");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const selectedMonth = dayjs(`${year}-${String(month).padStart(2, "0")}-01`);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);
            try {
                const res = await fetchRef.current(EMPLOYEE_MY_PENALTIES, { month, year });
                if (cancelled) return;
                if (res?.status === "success") {
                    setPenalties(res.data?.penalties || []);
                    setSummary(res.data?.summary || null);
                } else {
                    ToastMsg("error", res?.message || "Failed to load penalties");
                }
            } catch (err) {
                if (!cancelled) ToastMsg("error", err?.message || "Failed to load penalties");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [month, year]);

    const stepMonth = (delta) => {
        const d = selectedMonth.add(delta, "month");
        setMonth(d.month() + 1);
        setYear(d.year());
    };

    const openAppeal = (row) => {
        setSelected(row);
        setAppealType(
            row?.penalty_type?.toLowerCase().includes("late") ? "penalty_unfair" : "attendance_wrong",
        );
        setReason("");
        setAppealOpen(true);
    };

    const submitAppeal = async () => {
        if (!selected?.attendance_id) return;
        if ((reason || "").trim().length < 10) {
            ToastMsg("error", "Please enter a reason of at least 10 characters.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await createRef.current(PENALTY_APPEAL, {
                attendance_id: selected.attendance_id,
                appeal_type: appealType,
                reason: reason.trim(),
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Appeal submitted");
                setAppealOpen(false);
                const reload = await fetchRef.current(EMPLOYEE_MY_PENALTIES, { month, year });
                if (reload?.status === "success") {
                    setPenalties(reload.data?.penalties || []);
                    setSummary(reload.data?.summary || null);
                }
            } else {
                ToastMsg("error", res?.message || "Failed to submit appeal");
            }
        } catch (err) {
            ToastMsg("error", err?.message || "Failed to submit appeal");
        } finally {
            setSubmitting(false);
        }
    };

    const summaryCards = useMemo(() => [
        { label: "Penalty days", value: summary?.penalty_days ?? 0 },
        { label: "Fine total", value: `₹${Number(summary?.fine_total || 0).toLocaleString("en-IN")}` },
        { label: "LOP days", value: summary?.deduction_days ?? 0 },
        { label: "Pending appeals", value: summary?.pending_appeals ?? 0 },
    ], [summary]);

    return (
        <div className="space-y-3">
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => stepMonth(-1)}
                        className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/60"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => stepMonth(1)}
                        disabled={selectedMonth.isSame(dayjs(), "month") || selectedMonth.isAfter(dayjs(), "month")}
                        className={cn(
                            "h-9 w-9 inline-flex items-center justify-center rounded-lg border",
                            selectedMonth.isSame(dayjs(), "month") || selectedMonth.isAfter(dayjs(), "month")
                                ? "border-gray-200 text-gray-300 cursor-not-allowed"
                                : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/60",
                        )}
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                    {selectedMonth.format("MMMM YYYY")}
                </h3>
                {loading && <Loader2 className="w-4 h-4 animate-spin text-sidebar-primary" />}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                {summaryCards.map((c) => (
                    <div key={c.label} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400">{c.label}</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-1">{c.value}</p>
                    </div>
                ))}
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-sidebar-primary" />
                </div>
            ) : penalties.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 p-10 text-center text-sm text-gray-400">
                    No attendance penalties for {selectedMonth.format("MMMM YYYY")}.
                </div>
            ) : (
                <div className="space-y-2.5">
                    {penalties.map((row) => (
                        <div
                            key={row.attendance_id}
                            className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
                        >
                            <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <p className="font-semibold text-gray-900 dark:text-gray-100">{row.date_display}</p>
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                            {row.penalty_type}
                                        </span>
                                        <span className={cn(
                                            "text-xs px-2 py-0.5 rounded-full",
                                            row.penalty_mode === "fine"
                                                ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                                                : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
                                        )}>
                                            {row.penalty_label}
                                        </span>
                                    </div>
                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 flex items-center gap-1">
                                        {row.penalty_mode === "fine" ? (
                                            <IndianRupee className="w-3.5 h-3.5" />
                                        ) : null}
                                        {row.value_display}
                                        {row.late_minutes > 0 && (
                                            <span className="text-gray-400 font-normal">· {row.late_minutes} min late</span>
                                        )}
                                    </p>
                                    {row.reason && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{row.reason}</p>
                                    )}
                                    {row.appeal && (
                                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                            <span className={cn("px-2 py-0.5 rounded-full font-medium", STATUS_CHIP[row.appeal.status] || STATUS_CHIP.pending)}>
                                                Appeal: {row.appeal.status_label}
                                            </span>
                                            {row.appeal.admin_comment && (
                                                <span className="text-gray-500">HR: {row.appeal.admin_comment}</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {row.can_appeal && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="shrink-0"
                                        onClick={() => openAppeal(row)}
                                    >
                                        <MessageSquarePlus className="w-4 h-4 mr-1.5" />
                                        Raise concern
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="rounded-xl border border-amber-200/70 bg-amber-50/80 dark:bg-amber-950/20 dark:border-amber-900/40 px-4 py-3 text-xs text-amber-900 dark:text-amber-200 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <p>
                    If your attendance record is wrong, you can also submit an{" "}
                    <strong>Attendance Correction Request</strong> from Attendance Log.
                    Use <strong>Raise concern</strong> here to dispute a fine or LOP before payroll is finalized.
                </p>
            </div>

            <DialogModal
                open={appealOpen}
                onClose={() => setAppealOpen(false)}
                onSave={submitAppeal}
                title="Raise penalty concern"
                closeLabel="Cancel"
                saveLabel="Submit appeal"
                loading={submitting}
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        {selected?.date_display} · {selected?.penalty_type} · {selected?.value_display}
                    </p>
                    <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Concern type</label>
                        <Select
                            className="w-full mt-1"
                            value={appealType}
                            onChange={setAppealType}
                        >
                            {APPEAL_TYPES.map((t) => (
                                <Option key={t.value} value={t.value}>{t.label}</Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Reason</label>
                        <textarea
                            className="mt-1 w-full min-h-[100px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                            placeholder="Explain why this penalty should be reviewed (min 10 characters)"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </div>
                </div>
            </DialogModal>
        </div>
    );
};

export default MyProfilePenaltiesTab;
